import { Avatar } from '@radix-ui/themes';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import type { InlineSearchPlayer } from 'api/generated';
import { currentUser, isLoggedIn, isUserPremiumClubMember, isUserPremiumMember } from 'helpers/authentication';
import type { UserDetailShortType } from 'types/user';
import AgeCategoryLabel from './AgeCategoryLabel';
import MembershipLabel from './MembershipLabel';
import PlayerObjectSkeleton from './PlayerObjectSkeleton';
import RankLabel from './RankLabel';

export const PlayerUserObject: React.FC<
  Partial<InlineSearchPlayer> &
    Partial<UserDetailShortType> & {
      onClickFn?: (_p: Partial<InlineSearchPlayer>) => void;
      linkToPlayer?: boolean;
      showClub?: boolean;
      loading?: boolean;
      compact?: boolean;
      expandInMatchCard?: boolean;
      isSuperSimulation?: boolean;
      simulated_rank_higher?: boolean;
      showRole?: boolean;
      roles?: string;
      shrinkForComments?: boolean;
      stretch?: number;
    }
> = ({
  id,
  active,
  thumbnail,
  rank,
  category,
  premium,
  premium_expiration,
  user_exists,
  name,
  club_name,
  province,
  region,
  type,
  user_id,
  onClickFn,
  linkToPlayer = true,
  showClub = true,
  loading = false,
  compact = false,
  isSuperSimulation,
  simulated_rank_higher,
  showRole = false,
  roles,
  shrinkForComments = false,
  stretch,
}) => {
  if (loading || !id) {
    return <PlayerObjectSkeleton />;
  }
  let profilePath = '';

  if (isLoggedIn()) {
    if ((type === 'fit_player_profile' && currentUser().fit_player_profile_id === id) || currentUser().id === user_id)
      profilePath = '/profilo';
  }
  if (!profilePath && type === 'club_manager_profile') profilePath = `/circolo/${id}`;
  if (!profilePath && type === 'competition') profilePath = `/competizione/${id}`;
  if (!profilePath && type === 'fit_player_profile') profilePath = `/giocatore/${id}`;
  if (!profilePath) profilePath = `/utente/${user_id}`;

  const role = roles
    ? roles
    : type === 'fit_player_profile'
      ? 'Giocatore FITP'
      : type === 'club_manager_profile'
        ? 'Circolo di Tennis'
        : 'Talker';
  const nameClassName = classNames(
    'text-base w-full flex items-center',
    {
      'text-neutral-neutral-7': active === false,
    },
    { 'md:min-h-12': !compact },
    { 'md:min-h-10': compact },
  );

  const playerContentClassName = classNames('hover:cursor-pointer hover:bg-neutral-neutral-2', 'rounded-md min-w-0', {
    'py-2': !compact,
  });
  const firstLineClassName = classNames(
    'flex gap-2 items-center min-w-0 overflow-hidden',
    'max-w-full', // Ensure the container doesn't exceed its parent's width
    { 'p-2': !compact },
  );

  const addHyphens = (str: string, maxLen: number) => {
    let result = '';
    for (let i = 0; i < str.length; i++) {
      result += str[i];
      if ((i + 1) % maxLen === 0 && i !== str.length - 1) {
        result += '-';
      }
    }
    return result;
  };

  const getName = () => {
    if (!stretch) return name;
    let maxLen = 20;
    if (stretch > 450) maxLen = 18;
    if (stretch < 425) maxLen = 15;
    if (stretch < 400) maxLen = 10;
    if (stretch < 375) maxLen = 13;
    if (stretch < 330) maxLen = 11;
    if (stretch < 320) maxLen = 9;

    const words = name?.split(' ');
    words?.forEach((word) => {
      if (word.length > maxLen && !word.includes('-') && !word.includes("'")) {
        const hyphenated = addHyphens(word, maxLen);
        const index = words?.indexOf(word);
        words?.splice(index!, 1, hyphenated);
      }
    });
    return words?.join(' ');
  };

  const playerContent = (
    <div className={playerContentClassName}>
      <div className={firstLineClassName}>
        <div className='flex items-center'>
          <Avatar
            alt='Avatar Utente'
            className='h-8 w-8 rounded-md'
            src={thumbnail!}
            fallback={<img src='/images/player-avatar.jpg' />}
          />
        </div>
        {rank && (
          <div className='flex gap-1'>
            <RankLabel
              rank={rank}
              isSuperSimulation={isSuperSimulation}
              simulated_rank_higher={simulated_rank_higher}
              stretch={shrinkForComments}
            />
            {category && <AgeCategoryLabel ageCategory={category} stretch={shrinkForComments} />}
          </div>
        )}

        {shrinkForComments ? null : user_exists ? (
          <MembershipLabel
            premiumMember={isUserPremiumMember(premium!, premium_expiration as string)}
            premiumClubMember={isUserPremiumClubMember(premium!, premium_expiration as string)}
          />
        ) : (
          <MembershipLabel hidden={true} />
        )}

        <div className='min-w-0 flex-1'>
          <div className={nameClassName}>{getName()}</div>
        </div>
      </div>
      {showClub && club_name && (
        <div
          className={classNames(
            'text-neutral-neutral-9 block truncate px-2 text-xs font-normal',
            'max-w-72 min-w-0 overflow-hidden whitespace-nowrap md:max-w-full', // Ensure truncation
          )}
        >
          {region && province && `${region} (${province}) - `} {club_name}
        </div>
      )}
      {showRole && (
        <div className='text-neutral-neutral-7 relative inline-block w-auto max-w-full min-w-0 align-middle text-sm'>
          {role}
        </div>
      )}
    </div>
  );

  return linkToPlayer ? (
    <Link
      className='w-full max-w-full min-w-0'
      to={profilePath}
      onClick={() => {
        if (onClickFn) {
          onClickFn({
            id,
            name: name!,
            thumbnail: thumbnail!,
            rank: rank!,
            category: category!,
            club_name: club_name!,
            province: province!,
            region: region!,
            premium: premium!,
            premium_expiration: premium_expiration!,
            user_exists: user_exists!,
          });
        }
      }}
    >
      {playerContent}
    </Link>
  ) : (
    playerContent
  );
};
export default PlayerUserObject;
